import { IGroupInfo } from '@/api/types/product';
import { useReporting } from '@/hooks/useReporting';
import useRouteInfo from '@/hooks/useRouteInfo';
import {
  getSiteRedirectEnterPageScene,
  getSiteRedirectReferrerInfo,
} from '@/hooks/useSiteRedirect';
import { useStore } from '@/providers/app-store-provider';
import { ProviderType } from '@/reporting';
import logger from '@/utils/client-logger';
import { env } from '@/utils/env';
import { setBlockFlags, setGroupInfo } from '@/utils/feature-value';
import { getProductDisplayLabel, getProductImages, getWishNumber } from '@/utils/goods-list-utils';
import { genPrice } from '@/utils/price';
import { getCurrentScrollPercentage, getViewArea } from '@/utils/scroll';
import { getSession, removeSession } from '@/utils/tools';
import { useCallback, useEffect, useMemo, useRef } from 'react';
export const useGoodsListTrack = ({
  groupInfo,
  page,
  limit,
  isWeb,
  imageRule = [],
  imageHoverRule = [],
}: {
  groupInfo: IGroupInfo;
  page: number;
  limit: number;
  isWeb: boolean;
  imageRule: any;
  imageHoverRule: any;
}) => {
  const { trackEvent } = useReporting();
  const enterTimer = useRef<number>(Date.now());
  const goodsListEnterPageScene = useRef<string>('');
  const homePageFlagsInfo = useRef<string>('');
  const currency = useStore((state) => state.currency);
  const userInfo = useStore((state) => state.userInfo);
  const publicResourceData = useStore((state) => state.publicResourceData);
  const { getFromRouterInfo } = useRouteInfo();
  // 记录最后的滚动状态，避免页面跳转时滚动位置被重置
  const lastScrollState = useRef({ viewAdvance: 0, viewArea: 1 });
  const product_filter_new = useMemo(() => {
    try {
      return publicResourceData.config_names.product_filter_new;
    } catch {
      return [];
    }
  }, [publicResourceData]);

  const pageView = useCallback(
    (_fromRouterInfo: any) => {
      const sdEventName = 'ProductListPage_View';
      // 初始化进入页面场景值并立即清除session，避免影响其他页面
      const goodsListEnterPageSceneValue = getSession('zl_goodsListEnterPageScene') as string;
      const homePageFlagsInfoValue = getSession('zl_home_page_flags_info') as string;
      // 判断是否为直接进入（通过URL直接访问或刷新页面）
      const { refer_page_name, refer_url_path } = _fromRouterInfo;
      const isDirectEnter =
        !refer_url_path || !refer_page_name || refer_url_path === '' || refer_page_name === '';
      const isFromHomePage = refer_url_path === '/' || refer_page_name === '首页';

      if (goodsListEnterPageSceneValue) {
        goodsListEnterPageScene.current = goodsListEnterPageSceneValue;
        removeSession('zl_goodsListEnterPageScene');
      } else if (isDirectEnter) {
        goodsListEnterPageScene.current = '直接进入';
      } else if (isFromHomePage) {
        goodsListEnterPageScene.current = '首页坑位';
      } else {
        goodsListEnterPageScene.current = '其他';
      }

      if (homePageFlagsInfoValue) {
        homePageFlagsInfo.current = homePageFlagsInfoValue;
        removeSession('zl_home_page_flags_info');
      }

      if (window.location.pathname === '/AR-try-on') {
        goodsListEnterPageScene.current = 'AR页面引导';
      }

      const referrerInfo = getSiteRedirectReferrerInfo();
      const sdEventParams = {
        group_id: `${groupInfo.id}`,
        group_name: groupInfo.name,
        ad_display_pits_cnt: parseInt(String(groupInfo?.adv_count || 0), 10),
        refer_url_path: referrerInfo.referrer_url_path || _fromRouterInfo.refer_url_path,
        refer_page_name: referrerInfo.referrer_page_name || _fromRouterInfo.refer_page_name,
        enter_page_scene:
          getSiteRedirectEnterPageScene(referrerInfo) || goodsListEnterPageScene.current || '其他',
        from_site_info: referrerInfo.site_jump_from,
        page_flags_info: homePageFlagsInfo.current,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
      trackEvent(
        {
          eventName: 'category',
          properties: groupInfo.name,
        },
        [ProviderType.EMARSYS],
      );
    },
    [groupInfo, trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageBtn_Click
   * @EventDesc - 商品列表页按钮点击
   * @EventParams btn_name: string,
   * @Tapd 1008875
   */
  const SDProductListPageBtn_Click = useCallback(
    (btn_name: string) => {
      const sdEventName = 'ProductListPageBtn_Click';
      const sdEventParams = {
        group_id: `${groupInfo.id}`,
        group_name: groupInfo.name,
        btn_name,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    },
    [groupInfo, trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventDesc - 试戴按钮曝光
   */
  // const SD_TryONBtn_Exp = useCallback(
  //   (btn_name: string) => {
  //     try {
  //       const sdEventName = 'TryONBtn_Exp';
  //       const sdEventParams = {
  //         page_name: getRouteCnName(pathname),
  //         url_path: window.location.href,
  //         try_type: btn_name,
  //       };
  //       trackEvent(
  //         {
  //           eventName: sdEventName,
  //           properties: sdEventParams,
  //         },
  //         [ProviderType.SENSORS],
  //       );
  //     } catch (e) {
  //       logger.log(e);
  //     }
  //   },
  //   [pathname, trackEvent],
  // );

  // 若有model_url，则展示 ar tryon按钮（优先级高），若有tryon_image_url，则展示 tryon按钮，

  const handleARTryOn = (item: any) => {
    let flag = '';
    if (item.glb_model) {
      flag = 'ar-tryon';
    } else if (item.tryon_image_url) {
      flag = 'tryon';
    }
    return flag;
  };
  const getTryOnType = (item: any) => {
    if (item.glb_model && item.tryon_image_url) {
      return 'AR&Pictures';
    }
    if (item.glb_model) {
      return 'AR try on';
    }
    if (item.tryon_image_url) {
      return 'Try on with pictures';
    }
  };
  const getPrice = (item: any, _currency: string) => {
    if (
      userInfo.is_member === 1 &&
      item[`price_info_v${userInfo.member_level}`] &&
      item[`discount_percent_v${userInfo.member_level}`] > 0
    ) {
      return genPrice(item[`price_info_v${userInfo.member_level}`], _currency);
    }
    if (item.price_info) {
      return genPrice(item.price_info, _currency);
    }
    return { price: 0, sell_price: 0 };
  };
  const getGoodsListMktParams = useCallback(
    (params: any, index: number) => {
      const sd_image_type_list: ['', '商品图', '网红图', '素材图'] = [
        '',
        '商品图',
        '网红图',
        '素材图',
      ];
      const { productImage, hoverImage } = getProductImages(
        params,
        imageRule || undefined,
        imageHoverRule || undefined,
      );

      return {
        product_index: index,
        sort_index: params.sort_index,
        business_sku: params.business_sku,
        shop_sku: params.shop_sku,
        product_sku: params.sku,
        product_id: `${params.id}`,
        product_name: params.short_name || params.name,
        product_price: Number(getPrice(params, currency.name).price),
        current_product_price: Number(getPrice(params, currency.name).sell_price),
        base_current_product_price: Number(
          getPrice(params, env.NEXT_PUBLIC_DEFAULT_CURRENCY).sell_price,
        ),
        currency: currency.name,
        product_display_label: getProductDisplayLabel(params, 1),
        product_collect_num: getWishNumber(params),
        product_rating: parseFloat(params.spu_review_stars || '0'),
        product_review_num: parseInt(params.spu_review_count as string, 10),
        first_picture_url: productImage.src,
        second_picture_url: isWeb ? hoverImage.src : '',
        recall_group_type: params.recall_group_type,
        fine_ranking_type: params.fine_ranking_type,
        display_color_num: params.correlation?.length || 0,
        is_display_ar_try_on: handleARTryOn(params) === 'ar-tryon',
        image_type: sd_image_type_list[productImage.type || 0] || '',
        hover_type:
          isWeb && sd_image_type_list[hoverImage.type || 0]
            ? sd_image_type_list[hoverImage.type || 0]
            : '',
        dollar_exchange_rate: parseFloat(currency.exchange_rate),
        group_id: `${groupInfo?.id || ''}`,
        group_name: groupInfo?.name || '',
        tab_type: 'Product View',
        block_flags: 'goods_list@item_0@',
        block_flags_ext: JSON.stringify({ group_id: `${groupInfo.id}` }),
      };
    },
    [
      groupInfo,
      currency,
      isWeb,
      imageRule,
      imageHoverRule,
      getProductDisplayLabel,
      getWishNumber,
      handleARTryOn,
    ],
  );

  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageMkt_Exp
   * @EventDesc - 商品列表页坑位曝光
   * @Tapd 1008875
   */
  const sdProductListPageMktExp = useCallback((index: number, params: any) => {
    try {
      const sdEventName = 'ProductListPageMkt_Exp';
      const sdEventParams = getGoodsListMktParams(params, index);
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageValidMkt_Exp
   * @EventDesc - 商品列表页坑位曝光
   * @Tapd 1008875
   */
  const sdProductListPageMktValidExp = useCallback((index: number, params: any) => {
    try {
      const sdEventName = 'ProductListPageValidMkt_Exp';
      const sdEventParams = getGoodsListMktParams(params, index);
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageMkt_Click
   * @EventDesc - 商品列表页坑位点击
   * @Tapd 1008875
   */
  const SDProductListPageMkt_Click = useCallback(
    (params: any, index: number) => {
      try {
        const sdEventName = 'ProductListPageMkt_Click';
        const sdEventParams = getGoodsListMktParams(params, index);
        setBlockFlags({
          block_flags: sdEventParams.block_flags,
          block_flags_ext: sdEventParams.block_flags_ext,
          fine_ranking_type: sdEventParams.fine_ranking_type,
          recall_group_type: sdEventParams.recall_group_type,
          product_index: sdEventParams.product_index,
          first_picture_url: sdEventParams.first_picture_url,
        });
        setGroupInfo({
          group_id: `${groupInfo.id}`,
          group_name: groupInfo.name,
        });
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error(e);
      }
    },
    [getGoodsListMktParams, page, limit, trackEvent],
  );

  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageTryon_Click
   * @EventDesc - 商品列表页坑位试戴点击
   * @EventParams {
   *  try_type: string,
   *  product_index: number,
   *  shop_sku: string,
   *  business_sku: string,
   *  product_sku: string,
   *  product_id: string,
   *  product_name: string,
   *  tab_type: string,
   * }
   * @Tapd 1008875
   */
  const SDProductListPageTryon_Click = useCallback((params: any, index: number) => {
    try {
      const sdEventName = 'ProductListPageTryon_Click';
      const sdEventParams = {
        group_id: `${groupInfo.id}`,
        group_name: groupInfo.name,
        try_type: getTryOnType(params),
        ...getGoodsListMktParams(params, index),
      };
      sdEventParams.product_index += (page - 1) * limit;
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);

  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPage_Leave
   * @EventDesc - 商品列表页页面离开
   */
  const SDPageLeave = useCallback((_fromRouterInfo: any) => {
    try {
      const view_duration = Math.ceil((Date.now() - enterTimer.current) / 1000);
      const sdEventName = 'ProductListPage_Leave';
      const { viewAdvance, viewArea } = lastScrollState.current;

      const sdEventParams = {
        group_id: `${groupInfo.id}`,
        group_name: groupInfo.name,
        refer_url_path: _fromRouterInfo.refer_url_path,
        refer_page_name: _fromRouterInfo.refer_page_name,
        enter_page_scene:
          getSiteRedirectEnterPageScene() || goodsListEnterPageScene.current || '其他',
        page_flags_info: homePageFlagsInfo.current,
        view_advance: viewAdvance,
        view_duration,
        view_area: viewArea,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
      goodsListEnterPageScene.current = '其他';
    } catch (e) {
      logger.error(e);
    }
  }, []);
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageFilterSubmitBtn_Click
   * @EventDesc - 商品列表页筛选提交按钮点击
   * @EventParams {
   *  group_id: string,
   *  group_name: string,
   *  genders_list: [],
   *  size: string,
   *  price: string,
   *  type_list: []
   *  shape_list: []
   *  colour_list: []
   *  material_list: []
   *  lens_type_list: []
   * }
   * @ 57337
   */

  // 首字母大写辅助函数
  const capitalize = (str: string): string => {
    if (!str) return str;
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  // 创建value到name的映射函数
  const convertValueToName = useCallback(
    (attributeCode: string, values: string | string[]) => {
      if (!product_filter_new || !Array.isArray(product_filter_new)) {
        return values;
      }

      // 找到对应的属性配置
      const attributeConfig = product_filter_new.find(
        (config: any) => config.attribute_code === attributeCode,
      );

      if (!attributeConfig || !attributeConfig.attribute_values) {
        return values;
      }

      // 处理单个值
      if (typeof values === 'string') {
        const attributeValue = attributeConfig.attribute_values.find(
          (item: any) => item.value === values,
        );
        const result = attributeValue ? attributeValue.name : values;
        return capitalize(result);
      }

      // 处理数组值
      if (Array.isArray(values)) {
        return values.map((value: string) => {
          const attributeValue = attributeConfig.attribute_values.find(
            (item: any) => item.value === value,
          );
          const result = attributeValue ? attributeValue.name : value;
          return capitalize(result);
        });
      }

      return values;
    },
    [product_filter_new],
  );
  const sdProductListPageFilterSubmitBtnClick = useCallback(
    (filter: any) => {
      try {
        const params = {
          genders_list:
            filter?.product_gender && convertValueToName('product_gender', filter.product_gender),
          size: filter?.product_size && convertValueToName('product_size', filter.product_size),
          price: filter?.sell_price && convertValueToName('sell_price', filter.sell_price),
          shape_list:
            filter?.product_frame_shape &&
            convertValueToName('product_frame_shape', filter.product_frame_shape),
          colour_list:
            filter?.product_frame_color &&
            convertValueToName('product_frame_color', filter.product_frame_color),
          material_list:
            filter?.product_frame_material &&
            convertValueToName('product_frame_material', filter.product_frame_material),
          lens_type_list:
            filter?.prescription_type &&
            convertValueToName('prescription_type', filter.prescription_type),
        };
        const sdEventName = 'ProductListPageFilterSubmitBtn_Click';
        const sdEventParams = {
          group_id: `${groupInfo.id}`,
          group_name: groupInfo.name,
          ...params,
        };
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error(e);
      }
    },
    [convertValueToName, groupInfo, trackEvent],
  );
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageSortBtn_Click
   * @EventDesc - 商品列表页排序按钮点击
   * @EventParams {
   *  group_id: string,
   *  group_name: string,
   *  genders_list: [],
   *  size: string,
   *  price: string,
   *  type_list: []
   *  shape_list: []
   *  colour_list: []
   *  material_list: []
   *  lens_type_list: []
   * }
   * @ 57337
   */
  const sdProductListPageSortBtnClick = useCallback((newSort: string) => {
    try {
      const sdEventName = 'ProductListPageSortBtn_Click';
      const sdEventParams = {
        group_id: `${groupInfo.id}`,
        group_name: groupInfo.name,
        sort_name: newSort,
      };
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);

  const getBannerParams = (params: any) => {
    return {
      group_id: `${groupInfo.id}`,
      group_name: groupInfo.name,
      pits_index: 0,
      pits_jump_type: params.jump_type ? `${params.jump_type}` : '',
      pits_jump_content: params.jump_url ? `${params.jump_url}` : '',
    };
  };
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageBanner_Exp
   * @EventDesc - 商品列表页banner曝光
   * @EventParams {
   *  group_id: string,
   *  group_name: string,
   *  pits_index	坑位排序	NUMBER
   *  pits_jump_type	坑位内容的跳转类型	STRING
   *  pits_jump_content	坑位内容跳转的内容	STRING
   * }
   * @ 57337
   */
  const sdProductListPageBannerExp = useCallback((params: any) => {
    try {
      const sdEventName = 'ProductListPageBanner_Exp';
      const sdEventParams = getBannerParams(params);
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageBanner_Exp
   * @EventDesc - 商品列表页banner点击
   * @EventParams {
   *  group_id: string,
   *  group_name: string,
   *  pits_index	坑位排序	NUMBER
   *  pits_jump_type	坑位内容的跳转类型	STRING
   *  pits_jump_content	坑位内容跳转的内容	STRING
   * }
   * @ 57337
   */
  const sdProductListPageBannerClick = useCallback((params: any) => {
    try {
      const sdEventName = 'ProductListPageBanner_Click';
      const sdEventParams = getBannerParams(params);
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageRelatedGroup_Exp
   * @EventDesc - 商品列表页相关分组坑位曝光
   * @EventParams {
   *  group_id: string,
   *  group_name: string,
   *  belong_position	所属位置	STRING	顶部相关导航位/底部相关导航位
   *  btn_name	按钮名称	STRING
   *  pits_index	坑位排序	NUMBER
   *  pits_jump_type	内容的跳转类型	STRING
   *  pits_jump_content	跳转的内容	STRING
   * }
   * @ 57337
   */
  const getRelatedGroupParams = (params: any, index: number, belong_position: string) => {
    return {
      group_id: `${groupInfo.id}`,
      group_name: groupInfo.name,
      belong_position,
      btn_name: params.link_name,
      pits_index: index,
      pits_jump_type: '1',
      pits_jump_content: `${params.group_link}`,
    };
  };
  const sdProductListPageRelatedGroupExp = useCallback(
    (params: any, index: number, belong_position: string) => {
      try {
        const sdEventName = 'ProductListPageRelatedGroup_Exp';
        const sdEventParams = getRelatedGroupParams(params, index, belong_position);
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error(e);
      }
    },
    [],
  );
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageRelatedGroup_Click
   * @EventDesc - 商品列表页相关分组坑位点击
   * @EventParams {
   *  group_id: string,
   *  group_name: string,
   *  belong_position	所属位置	STRING	顶部相关导航位/底部相关导航位
   *  btn_name	按钮名称	STRING
   *  pits_index	坑位排序	NUMBER
   *  pits_jump_type	内容的跳转类型	STRING
   *  pits_jump_content	跳转的内容	STRING
   * }
   * @ 57337
   */
  const sdProductListPageRelatedGroupClick = useCallback(
    (params: any, index: number, belong_position: string) => {
      try {
        const sdEventName = 'ProductListPageRelatedGroup_Click';
        const sdEventParams = getRelatedGroupParams(params, index, belong_position);
        trackEvent(
          {
            eventName: sdEventName,
            properties: sdEventParams,
          },
          [ProviderType.SENSORS],
        );
      } catch (e) {
        logger.error(e);
      }
    },
    [],
  );
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageAD_Exp
   * @EventDesc - 商品列表页广告坑位曝光
   * @EventParams {
   *  tab_type	tab类型	STRING
   *  group_id	分组id	STRING
   *  group_name	分组名称	STRING
   *  pits_index	坑位排序	NUMBER
   *  ad_pits_id	广告坑位id	STRING
   *  ad_position	魔方配置的广告位置	STRING
   *  pits_jump_type	内容的跳转类型	STRING
   *  pits_jump_content	跳转的内容	STRING
   * }
   * @ 57337
   */
  const getADParams = (item: any, index: number) => {
    return {
      group_id: `${groupInfo.id}`,
      group_name: groupInfo.name,
      tab_type: 'Product View',
      pits_index: index,
      ad_pits_id: `${item.adv_id}`,
      ad_position: `${item.position}`,
      pits_jump_type: `${item.pits_jump_info?.jump_type}`,
      pits_jump_content: `${item.pits_jump_info?.Jump_target}`,
    };
  };
  const sdProductListPageADExp = useCallback((item: any, index: number) => {
    try {
      const sdEventName = 'ProductListPageAD_Exp';
      const sdEventParams = getADParams(item, index);
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);
  /**
   * @TrackType sensorsdata
   * @EventType - ProductListPageAD_Exp
   * @EventDesc - 商品列表页广告坑位点击
   * @EventParams {
   *  tab_type	tab类型	STRING
   *  group_id	分组id	STRING
   *  group_name	分组名称	STRING
   *  pits_index	坑位排序	NUMBER
   *  ad_pits_id	广告坑位id	STRING
   *  ad_position	魔方配置的广告位置	STRING
   *  pits_jump_type	内容的跳转类型	STRING
   *  pits_jump_content	跳转的内容	STRING
   * }
   * @ 57337
   */
  const sdProductListPageADClick = useCallback((params: any, index: number) => {
    try {
      const sdEventName = 'ProductListPageAD_Click';
      const sdEventParams = getADParams(params, index);
      trackEvent(
        {
          eventName: sdEventName,
          properties: sdEventParams,
        },
        [ProviderType.SENSORS],
      );
    } catch (e) {
      logger.error(e);
    }
  }, []);
  useEffect(() => {
    enterTimer.current = Date.now();
    const fromRouterInfo = getFromRouterInfo();
    pageView(fromRouterInfo);
    return () => {
      SDPageLeave(fromRouterInfo);
      goodsListEnterPageScene.current = '其他';
    };
  }, []);
  // 定期更新滚动状态，避免页面跳转时丢失滚动信息
  useEffect(() => {
    const updateScrollState = () => {
      try {
        const viewAdvance = getCurrentScrollPercentage();
        const viewArea = getViewArea();
        lastScrollState.current = { viewAdvance, viewArea };
      } catch (error) {
        // 忽略错误，使用默认值
        logger.error('Error updating scroll state:', error);
      }
    };

    // 立即更新一次
    updateScrollState();

    // 监听滚动事件，实时更新滚动状态
    const handleScroll = () => {
      updateScrollState();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  return {
    pageView,
    SDPageLeave,
    sdProductListPageADClick,
    sdProductListPageADExp,
    sdProductListPageBannerClick,
    sdProductListPageBannerExp,
    sdProductListPageRelatedGroupClick,
    sdProductListPageRelatedGroupExp,
    sdProductListPageSortBtnClick,
    sdProductListPageFilterSubmitBtnClick,
    SDProductListPageBtn_Click,
    SDProductListPageMkt_Click,
    sdProductListPageMktExp,
    sdProductListPageMktValidExp,
    SDProductListPageTryon_Click,
  };
};
